import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  Clock,
  Copy,
  Download,
  FileLock2,
  Lock,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  Badge,
  Button,
  Card,
  ConfirmationDialog,
  EmptyState,
  Label,
  Progress,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TextInput,
} from '../../components/ui';
import {
  bufferToBase64,
  buildShareUrl,
  deriveKeys,
  encryptFileToChunks,
  encryptMetadata,
  encryptSecretForUser,
  decryptSecretForUser,
  generateSecret,
} from './crypto';
import {
  completeFileSend,
  createFileSend,
  deleteFileSend,
  fetchFileSendLimits,
  fetchMyFileSends,
  FileSendLimits,
  FileSendShareListItem,
  forgetShareUrl,
  loadOwnerToken,
  loadShareUrl,
  storeOwnerToken,
  storeShareUrl,
  uploadFileSendChunk,
} from './useData';

const EXPIRE_LABELS: Record<number, string> = {
  3600: '1 hour',
  86400: '1 day',
  259200: '3 days',
  604800: '7 days',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** Short human distance, e.g. "in 3 days" / "2 h ago". */
function formatRelative(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const past = diffMs < 0;
  const mins = Math.round(Math.abs(diffMs) / 60000);
  const value =
    mins < 60
      ? `${Math.max(1, mins)} min`
      : mins < 1440
        ? `${Math.round(mins / 60)} h`
        : `${Math.round(mins / 1440)} d`;
  return past ? `${value} ago` : `in ${value}`;
}

const FileSendUploadPage: React.FC = () => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const userId = user?.id || 0;
  const [searchParams] = useSearchParams();
  const composeMode = searchParams.get('compose') === '1';

  const [limits, setLimits] = useState<FileSendLimits | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [expireSeconds, setExpireSeconds] = useState(86400);
  const [maxDownloads, setMaxDownloads] = useState(1);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareId, setShareId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [shares, setShares] = useState<FileSendShareListItem[]>([]);
  const [sharesLoading, setSharesLoading] = useState(true);
  const [sharesError, setSharesError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('create');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const refreshShares = useCallback(async () => {
    try {
      setSharesError(null);
      const rows = await fetchMyFileSends();
      
      // Decrypt secrets for cross-device access
      const rowsWithUrls = await Promise.all(
        rows.map(async (row) => {
          let shareUrl = loadShareUrl(row.id); // Try localStorage first (fast)
          
          // If not in localStorage and we have an encrypted secret, decrypt it
          if (!shareUrl && row.encryptedSecret && userId) {
            try {
              const secretFragment = await decryptSecretForUser(row.encryptedSecret, userId);
              shareUrl = buildShareUrl(window.location.origin, row.id, secretFragment);
            } catch (e) {
              console.warn(`Failed to decrypt secret for ${row.id}:`, e);
            }
          }
          
          return { ...row, shareUrl };
        })
      );
      
      setShares(rowsWithUrls as any);
    } catch (e) {
      setSharesError((e as Error)?.message || 'Failed to load your links');
    } finally {
      setSharesLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    fetchFileSendLimits()
      .then((l) => {
        if (cancelled) return;
        setLimits(l);
        setExpireSeconds(l.defaultExpireSeconds || 86400);
        setMaxDownloads(l.defaultMaxDownloads || 1);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || 'Failed to load limits');
      });
    void refreshShares();
    return () => {
      cancelled = true;
    };
  }, [refreshShares]);

  const expireOptions = useMemo(() => {
    const max = limits?.maxExpireSeconds ?? 604800;
    const presets = limits?.expirePresetsSeconds ?? [3600, 86400, 259200, 604800];
    return presets.filter((s) => s <= max);
  }, [limits]);

  const onFile = useCallback(
    (f: File | null) => {
      setError(null);
      setShareUrl(null);
      setShareId(null);
      if (!f) {
        setFile(null);
        return;
      }
      const max = limits?.maxSizeBytes ?? 1073741824;
      if (f.size > max) {
        setError(`File exceeds maximum size of ${formatBytes(max)}`);
        setFile(null);
        return;
      }
      setFile(f);
    },
    [limits],
  );

  const notifyCompose = useCallback((url: string) => {
    if (!composeMode) return;
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: 'ucomm-file-send', url }, window.location.origin);
    }
  }, [composeMode]);

  const startUpload = async () => {
    if (!file || !limits) return;
    setBusy(true);
    setError(null);
    setProgress(0);
    setStatus('Creating share…');
    try {
      const created = await createFileSend({
        expireSeconds,
        maxDownloads,
        password: password.trim() || undefined,
      });
      storeOwnerToken(created.id, created.ownerToken);
      setShareId(created.id);

      const { secret, secretFragment } = generateSecret();
      const keys = await deriveKeys(secret);
      const encryptedMeta = await encryptMetadata(keys.metaKey, {
        filename: file.name,
        mime: file.type || 'application/octet-stream',
        size: file.size,
      });

      setStatus('Encrypting and uploading…');
      let chunkIndex = 0;
      const maxChunk = created.maxChunkBytes || limits.maxChunkBytes;
      let buffer = new Uint8Array(0);

      const flush = async (force = false) => {
        while (buffer.length >= maxChunk || (force && buffer.length > 0)) {
          const take = Math.min(buffer.length, maxChunk);
          const piece = buffer.slice(0, take);
          buffer = buffer.slice(take);
          await uploadFileSendChunk(created.id, created.uploadToken, chunkIndex, piece);
          chunkIndex += 1;
        }
      };

      for await (const piece of encryptFileToChunks(file, keys.fileKey, (p) => {
        setProgress(Math.round((p.bytesDone / p.bytesTotal) * 100));
      })) {
        const merged = new Uint8Array(buffer.length + piece.length);
        merged.set(buffer, 0);
        merged.set(piece, buffer.length);
        buffer = merged;
        await flush(false);
      }
      await flush(true);

      setStatus('Finalizing…');
      
      // Encrypt secret for cross-device sync (if userId available)
      let encryptedSecret: string | undefined;
      if (userId) {
        try {
          encryptedSecret = await encryptSecretForUser(secretFragment, userId);
        } catch (e) {
          console.warn('Failed to encrypt secret for sync:', e);
        }
      }
      
      await completeFileSend(created.id, created.uploadToken, {
        encryptedMetaBase64: bufferToBase64(encryptedMeta),
        authKeyBase64: bufferToBase64(keys.authKeyRaw),
        sizeBytes: file.size,
        originalFilename: file.name,
        encryptedSecret,
      });

      const url = buildShareUrl(window.location.origin, created.id, secretFragment);
      storeShareUrl(created.id, url);
      setShareUrl(url);
      setStatus('Done');
      notifyCompose(url);
      await refreshShares();
      
      // Auto-switch to "My Links" tab and highlight the new item
      if (!composeMode) {
        setActiveTab('links');
        setHighlightedId(created.id);
        setTimeout(() => setHighlightedId(null), 3000);
      }
    } catch (e) {
      setError((e as Error)?.message || 'Upload failed');
      setStatus(null);
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (url: string, id?: string) => {
    try {
      await navigator.clipboard.writeText(url);
      if (id) {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    await copyText(shareUrl);
  };

  const removeShare = async (id: string) => {
    setDeletingId(id);
    setError(null);
    try {
      await deleteFileSend(id, loadOwnerToken(id) ?? undefined);
      forgetShareUrl(id);
      if (shareId === id) {
        setShareUrl(null);
        setStatus('Share deleted from server');
      }
      await refreshShares();
      setConfirmDeleteId(null);
    } catch (e) {
      setError((e as Error)?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const confirmTarget = useMemo(
    () => shares.find((s) => s.id === confirmDeleteId) ?? null,
    [shares, confirmDeleteId],
  );

  return (
    <div className={cn('min-h-screen', colors.background)}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {composeMode && (
        <div className={cn('mb-6 rounded-lg border px-4 py-3 text-sm', colors.border, 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800')}>
          <p className="text-blue-800 dark:text-blue-200">
            <strong>Compose mode:</strong> The link will be automatically sent back to the compose window after upload.
          </p>
        </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="links">
            <FileLock2 className="mr-2 h-4 w-4" aria-hidden />
            My Links
            {shares.length > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">
                {shares.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="create">
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Link
          </TabsTrigger>
        </TabsList>

        {error && (
          <div className="mb-4 rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <TabsContent value="create">
          <div className="space-y-6">
      <Card
        className="border-2 border-dashed p-12 text-center shadow-none hover:border-primary/50 transition-colors"
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        }}
      >
        <input
          type="file"
          className="hidden"
          id="file-send-input"
          disabled={busy}
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <label htmlFor="file-send-input" className="cursor-pointer block">
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileLock2 className="h-8 w-8 text-primary" aria-hidden />
              <div className="text-left">
                <p className="font-semibold text-base">{file.name}</p>
                <p className={cn('text-sm', colors.textMuted)}>{formatBytes(file.size)}</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-3 flex justify-center">
                <div className={cn('rounded-full p-4', 'bg-primary/10')}>
                  <svg className="h-8 w-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
              </div>
              <p className="text-base font-medium mb-1">Drop a file here or click to browse</p>
              <p className={cn('text-sm', colors.textMuted)}>Maximum file size: {limits ? formatBytes(limits.maxSizeBytes) : '...'}</p>
            </div>
          )}
        </label>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <Label htmlFor="file-send-expire" required>
            Expires
          </Label>
          <Select
            name="file-send-expire"
            value={String(expireSeconds)}
            disabled={busy}
            searchable={false}
            showClearButton={false}
            dropdownMinWidth={0}
            onChange={(e) => setExpireSeconds(Number(e.target.value))}
            options={expireOptions.map((s) => ({
              value: String(s),
              label: EXPIRE_LABELS[s] ?? `${s}s`,
            }))}
          />
        </div>
        <div>
          <Label htmlFor="file-send-downloads">Download limit</Label>
          <Select
            name="file-send-downloads"
            value={String(maxDownloads)}
            disabled={busy}
            searchable={false}
            showClearButton={false}
            dropdownMinWidth={0}
            onChange={(e) => setMaxDownloads(Number(e.target.value))}
            options={(limits?.downloadPresets ?? [1, 2, 3, 5, 10, 20]).map((n) => ({
              value: String(n),
              label: `${n} download${n === 1 ? '' : 's'}`,
            }))}
          />
        </div>
        <div>
          <Label htmlFor="file-send-password">Password (optional)</Label>
        <TextInput
          name="file-send-password"
          type="password"
          autoComplete="new-password"
          value={password}
          disabled={busy}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Extra gate before download"
          helperText="Anyone with the full link and this password can download."
        />
        </div>
      </div>

      <div className="space-y-4">
      <Button
        type="button"
        className="w-full h-12 text-base font-semibold"
        disabled={!file || !limits}
        loading={busy}
        onClick={() => void startUpload()}
      >
        {busy ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Encrypting... {progress}%
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <Lock className="h-5 w-5" aria-hidden />
            Encrypt & Create Link
          </span>
        )}
      </Button>

      {busy && (
        <div className="space-y-2">
          <Progress value={progress} />
          <p className={cn('text-center text-sm', colors.textMuted)}>
            Encrypting your file... This happens in your browser.
          </p>
        </div>
      )}
      {status && !busy && <p className={cn('text-center text-sm font-medium text-green-600 dark:text-green-400')}>{status}</p>}
      </div>

        {shareUrl && (
        <Card className="p-6 space-y-4 bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-green-100 dark:bg-green-900/50 p-3">
              <Check className="h-6 w-6 text-green-600 dark:text-green-400" aria-hidden />
            </div>
            <div>
              <p className="font-semibold text-lg text-green-700 dark:text-green-300">Link Created Successfully!</p>
              <p className={cn('text-sm', colors.textMuted)}>Share this link to send your encrypted file</p>
            </div>
          </div>
          <div className={cn('rounded-lg p-4 font-mono text-sm break-all', colors.surface, 'border', colors.border)}>
            {shareUrl}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => void copyLink()}>
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                  Copy link
                </>
              )}
            </Button>
            {composeMode && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  notifyCompose(shareUrl);
                  window.close();
                }}
              >
                Insert & close
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDeleteId(shareId)}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              Delete from server
            </Button>
          </div>
        </Card>
        )}
          </div>
        </TabsContent>

        <TabsContent value="links">
          <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <FileLock2 className={cn('h-5 w-5', colors.textMuted)} aria-hidden />
            <p className={cn('text-sm', colors.textMuted)}>
              Links are synced across your devices
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => void refreshShares()}
          >
            <RefreshCw className="h-4 w-4 mr-2" aria-hidden />
            Refresh
          </Button>
        </div>

        {sharesError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {sharesError}
          </div>
        )}

        {sharesLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="h-32 animate-pulse">
                <span className="sr-only">Loading</span>
              </Card>
            ))}
          </div>
        ) : shares.length === 0 ? (
          <EmptyState
            icon={<FileLock2 className={cn('mx-auto mb-2 h-12 w-12 opacity-40', colors.textMuted)} aria-hidden />}
            title="No links yet"
            description="Create a secure link in the 'Create Link' tab to get started."
          />
        ) : (
          <div className="grid gap-4">
            {shares.map((row: any) => {
              const savedUrl = row.shareUrl; // Now comes from refreshShares
              const name = row.originalFilename || 'Encrypted file';
              const usedPct = Math.min(
                100,
                Math.round((row.downloadCount / Math.max(1, row.maxDownloads)) * 100),
              );
              const badge: { label: string; variant: 'success' | 'warning' | 'secondary' } = row.expired
                ? { label: 'Expired', variant: 'warning' }
                : row.exhausted
                  ? { label: 'Limit reached', variant: 'secondary' }
                  : { label: 'Active', variant: 'success' };

              return (
                <Card
                  key={row.id}
                  className={cn(
                    'p-6 transition-all duration-500',
                    colors.hoverSurface,
                    highlightedId === row.id && 'ring-2 ring-primary shadow-lg',
                  )}
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-1 min-w-0 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div
                            className={cn(
                              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                              row.active
                                ? 'bg-primary/10 text-primary'
                                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                            )}
                          >
                            <FileLock2 className="h-5 w-5" aria-hidden />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-lg font-semibold mb-1" title={name}>
                              {name}
                            </p>
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                              <span className={cn('flex items-center gap-1', colors.textMuted)}>
                                <Clock className="h-3.5 w-3.5" aria-hidden />
                                Created {formatRelative(row.createdAt)}
                              </span>
                              <span className={cn('flex items-center gap-1', colors.textMuted)}>
                                {formatBytes(row.sizeBytes)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                        <Badge variant={badge.variant} className="shrink-0">
                          {badge.label}
                        </Badge>
                        {row.passwordRequired && (
                          <div className="flex items-center gap-1.5 rounded-md bg-yellow-100 dark:bg-yellow-900/30 px-2 py-0.5">
                            <Lock className="h-3 w-3 text-yellow-700 dark:text-yellow-400" aria-hidden />
                            <span className="text-xs font-medium text-yellow-700 dark:text-yellow-400">
                              Password
                            </span>
                          </div>
                        )}
                        </div>
                      </div>

                      <div className="flex items-center gap-6 text-sm">
                        <span className={cn('flex items-center gap-1.5', colors.textMuted)}>
                          <Download className="h-4 w-4" aria-hidden />
                          <span>
                            {row.downloadCount} / {row.maxDownloads} downloads
                          </span>
                        </span>
                        <span className={cn('flex items-center gap-1.5', colors.textMuted)}>
                          <Clock className="h-4 w-4" aria-hidden />
                          Expires {formatRelative(row.expiresAt)}
                        </span>
                      </div>

                      {row.maxDownloads > 1 && !row.exhausted && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className={colors.textMuted}>Download usage</span>
                            <span className={cn('font-semibold', colors.text)}>
                              {usedPct}%
                            </span>
                          </div>
                          <Progress value={usedPct} size="sm" />
                        </div>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {savedUrl ? (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={!savedUrl}
                          onClick={() => savedUrl && void copyText(savedUrl, row.id)}
                        >
                          {copiedId === row.id ? (
                            <>
                              <Check className="h-4 w-4 mr-2" aria-hidden />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" aria-hidden />
                              Copy Link
                            </>
                          )}
                        </Button>
                      ) : (
                        <span className={cn('text-sm', colors.textMuted)}>Link not available</span>
                      )}
                      {composeMode && savedUrl && row.active && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            notifyCompose(savedUrl);
                            window.close();
                          }}
                        >
                          Insert
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        disabled={deletingId === row.id}
                        onClick={() => setConfirmDeleteId(row.id)}
                        className="text-red-600 hover:bg-red-50 hover:border-red-300 dark:text-red-400 dark:hover:bg-red-950/30 dark:hover:border-red-800"
                      >
                        <Trash2 className="h-4 w-4 mr-2" aria-hidden />
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmationDialog
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => {
          if (confirmDeleteId) void removeShare(confirmDeleteId);
        }}
        isLoading={deletingId !== null}
        title="Delete this link?"
        message={
          confirmTarget?.originalFilename
            ? `“${confirmTarget.originalFilename}” will be removed from the server and the link will stop working.`
            : 'The encrypted file will be removed from the server and the link will stop working.'
        }
        warningMessage="This cannot be undone."
        confirmText="Delete"
      />
      </div>
    </div>
  );
};

export default FileSendUploadPage;
